import { Input, Label, Modal, Select, Textarea } from '@crm/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, type AutocompleteOption } from '@/components';
import { toErrorMessage } from '@/modules/auth/utils';
import { useUnits } from '@/modules/company/hooks';
import { CONVERSATIONS_ROUTES } from '@/modules/conversations/api';
import { useManualMessage } from '../api';

/**
 * Manda mensagem para quem ainda nao escreveu. O contato pode sair da base ou
 * ser digitado na hora - nesse caso o cadastro e criado junto, senao o balcao
 * teria de sair da tela para falar com alguem novo.
 */
export function ManualMessageModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { data: units } = useUnits();
  const enviar = useManualMessage();

  const [unitId, setUnitId] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [escolhido, setEscolhido] = useState<AutocompleteOption[]>([]);
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [texto, setTexto] = useState('');

  const unidades = units?.data ?? [];
  const unidadeEscolhida = unitId || unidades[0]?.id || '';
  const pronto = Boolean(unidadeEscolhida && texto.trim() && (customerId || telefone.trim()));

  async function confirmar() {
    const resultado = await enviar.mutateAsync({
      unitId: unidadeEscolhida,
      customerId: customerId ?? undefined,
      name: customerId ? undefined : nome.trim() || undefined,
      phone: customerId ? undefined : telefone.trim(),
      body: texto.trim(),
    });

    onClose();
    navigate(`${CONVERSATIONS_ROUTES.LIST}?c=${resultado.conversationId}`);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Enviar mensagem"
      subtitle="Escolha um cliente ou informe nome e WhatsApp"
      size="medium"
      actions={[
        { label: 'Cancelar', variant: 'outline', onClick: onClose },
        {
          label: 'Enviar',
          onClick: () => void confirmar(),
          disabled: !pronto,
          loading: enviar.isPending,
        },
      ]}
    >
      <div className="space-y-4">
        {unidades.length > 1 ? (
          <div className="space-y-1">
            <Label>Unidade</Label>
            <Select value={unidadeEscolhida} onChange={(event) => setUnitId(event.target.value)}>
              {unidades.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}

        <Autocomplete
          label="Cliente"
          placeholder="Procure pelo nome"
          url="/api/v1/customers"
          emptyLabel="Nenhum cliente escolhido"
          value={escolhido}
          mapOption={(row) => ({
            id: String(row.id),
            label: String(row.name ?? ''),
            hint: row.phone ? String(row.phone) : undefined,
          })}
          onChange={(options) => {
            const option = options[0];
            setEscolhido(option ? [option] : []);
            setCustomerId(option?.id ?? null);
            setNome(option?.label ?? '');
          }}
        />

        {customerId ? null : (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Nome</Label>
              <Input value={nome} onChange={(event) => setNome(event.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>WhatsApp</Label>
              <Input
                value={telefone}
                placeholder="11 99999-9999"
                onChange={(event) => setTelefone(event.target.value)}
              />
            </div>
          </div>
        )}

        <div className="space-y-1">
          <Label>Mensagem</Label>
          <Textarea rows={4} value={texto} onChange={(event) => setTexto(event.target.value)} />
        </div>

        {enviar.error ? (
          <p className="text-sm text-destructive">{toErrorMessage(enviar.error)}</p>
        ) : null}
      </div>
    </Modal>
  );
}
